import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Heading } from "~/components/ui/heading";
import { Button } from "~/components/ui/button";
import { coachQueryOptions } from "~/lib/query-options";
import { FoundingCoachCard } from "~/components/founding-coach-card";

export const Route = createFileRoute("/_public/coaches/founding/")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(coachQueryOptions.founding());
    return {};
  },
  head: () => ({
    title: "Founding Coaches | Dream Coach Match",
    meta: [
      {
        name: "description",
        content:
          "Meet the founding coaches who shaped Dream Coach Match. Discover their proven expertise and transformational approaches.",
      },
    ],
  }),
  component: FoundingCoachesPage,
});

function FoundingCoachesPage() {
  const {
    data: foundingCoaches,
    error,
    isLoading,
  } = useQuery(coachQueryOptions.founding());

  if (isLoading) {
    return <div className="py-16 text-center text-gray-500">Loading founding coaches…</div>;
  }

  if (error) {
    return (
      <div className="py-16 text-center text-red-600">
        We couldn't load our founding coaches. Please refresh the page and try again.
      </div>
    );
  }

  if (!foundingCoaches) {
    return null;
  }

  return (
    <div className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-brand-25 via-white to-white" aria-hidden="true" />
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <Heading
          isH1
          preHeadline="The visionaries behind Dream Coach Match"
          headline="Meet our founding coaches"
          subheadline="These trailblazing coaches partnered with us from day one. They set the standard for excellence, shape our community, and continue to deliver transformational results to clients around the world."
        />
        <div className="mt-10 grid gap-10 lg:grid-cols-2">
          {foundingCoaches.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-brand-200 bg-white/70 p-12 text-center text-lg text-gray-600">
              Founding coach profiles are being curated. Check back soon to meet the leaders of our community.
            </div>
          ) : (
            foundingCoaches.map((coach) => <FoundingCoachCard key={coach.id} coach={coach} />)
          )}
        </div>
        <div className="mt-16 flex justify-center">
          <Link to="/discovery">
            <Button size="lg" variant="fancy">
              Find your coach
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
